import { Loading, Scrollable } from '@affine/component';
import { WorkspaceDetailSkeleton } from '@affine/component/setting-components';
import type { ModalProps } from '@affine/component/ui/modal';
import { Modal } from '@affine/component/ui/modal';
import {
  AuthService,
  DefaultServerService,
  ServersService,
} from '@affine/core/modules/cloud';
import type { DialogComponentProps } from '@affine/core/modules/dialogs';
import type {
  SettingTab,
  WORKSPACE_DIALOG_SCHEMA,
} from '@affine/core/modules/dialogs/constant';
import { GlobalContextService } from '@affine/core/modules/global-context';
import { createIsland, type Island } from '@affine/core/utils/island';
import { useI18n } from '@affine/i18n';
import { SidebarIcon } from '@blocksuite/icons/rc';
// STREAMLAIN: Footer hidden - imports commented out
// import { Trans } from '@affine/i18n';
// import { ContactWithUsIcon } from '@blocksuite/icons/rc';
import { FrameworkScope, useLiveData, useService } from '@toeverything/infra';
import { debounce } from 'lodash-es';
import {
  Suspense,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { flushSync } from 'react-dom';
import type { FallbackProps } from 'react-error-boundary';

import { SWRErrorBoundary } from '../../../components/pure/swr-error-bundary';
import { AccountSetting } from './account-setting';
import { GeneralSetting } from './general-setting';
// STREAMLAIN: Footer hidden - imports commented out
// import { IssueFeedbackModal } from './issue-feedback-modal';
import { SettingSidebar } from './setting-sidebar';
// import { StarAFFiNEModal } from './star-affine-modal';
import * as style from './style.css';
import {
  SubPageContext,
  type SubPageContextType,
  SubPageTarget,
} from './sub-page';
import type { SettingState } from './types';
import { WorkspaceSetting } from './workspace-setting';

interface SettingProps extends ModalProps {
  activeTab?: SettingTab;
  mailAccountId?: string;
  onCloseSetting: () => void;
  scrollAnchor?: string;
}

const isWorkspaceSetting = (key: string): boolean =>
  key.startsWith('workspace:');

// Tabs that render wide data tables / grids and need to break out of the
// 560px reading-column clamp on `.content`.
const WIDE_TABS = new Set<string>([
  'workspace:audit-trail',
  'workspace:permission-graph',
]);

const CenteredLoading = () => {
  return (
    <div className={style.centeredLoading}>
      <Loading size={24} />
    </div>
  );
};

const SettingsPanelErrorFallback = ({ resetErrorBoundary }: FallbackProps) => {
  const t = useI18n();
  return (
    <div
      style={{
        display: 'flex',
        minHeight: 280,
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 12,
        textAlign: 'center',
      }}
    >
      <div
        style={{
          color: 'var(--streamlain-text-primary-color)',
          fontSize: 18,
          fontWeight: 600,
        }}
      >
        {t['com.streamlain.settings.connection-required.title']()}
      </div>
      <div
        style={{
          maxWidth: 420,
          color: 'var(--streamlain-text-secondary-color)',
          fontSize: 14,
          lineHeight: 1.5,
        }}
      >
        {t['com.streamlain.settings.connection-required.description']()}
      </div>
      <button
        type="button"
        onClick={resetErrorBoundary}
        style={{
          marginTop: 4,
          cursor: 'pointer',
          border: 0,
          background: 'transparent',
          color: 'var(--streamlain-primary-color, #1e96eb)',
          fontSize: 14,
          fontWeight: 600,
        }}
      >
        {t['com.streamlain.error.retry']()}
      </button>
    </div>
  );
};

const SettingModalInner = ({
  activeTab: initialActiveTab = 'appearance',
  mailAccountId,
  onCloseSetting,
  scrollAnchor: initialScrollAnchor,
}: SettingProps) => {
  const [subPageIslands, setSubPageIslands] = useState<Island[]>([]);
  const [settingState, setSettingState] = useState<SettingState>({
    activeTab: initialActiveTab,
    scrollAnchor: initialScrollAnchor,
  });
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const globalContextService = useService(GlobalContextService);

  const currentServerId = useLiveData(
    globalContextService.globalContext.serverId.$
  );
  const serversService = useService(ServersService);
  const defaultServerService = useService(DefaultServerService);
  const currentServer =
    useLiveData(
      useMemo(
        () =>
          currentServerId ? serversService.server$(currentServerId) : null,
        [currentServerId, serversService]
      )
    ) ?? defaultServerService.server;
  const loginStatus = useLiveData(
    currentServer.scope.get(AuthService).session.status$
  );
  const modalContentRef = useRef<HTMLDivElement>(null);
  const modalContentWrapperRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    let animationFrameId: number;
    const onResize = debounce(() => {
      cancelAnimationFrame(animationFrameId);
      animationFrameId = requestAnimationFrame(() => {
        if (!modalContentRef.current || !modalContentWrapperRef.current) return;

        const wrapperWidth = modalContentWrapperRef.current.offsetWidth;
        const wrapperHeight = modalContentWrapperRef.current.offsetHeight;
        const contentWidth = modalContentRef.current.offsetWidth;

        const wrapper = modalContentWrapperRef.current;

        wrapper?.style.setProperty(
          '--setting-modal-width',
          `${wrapperWidth}px`
        );
        wrapper?.style.setProperty(
          '--setting-modal-height',
          `${wrapperHeight}px`
        );
        wrapper?.style.setProperty(
          '--setting-modal-content-width',
          `${contentWidth}px`
        );
        wrapper?.style.setProperty(
          '--setting-modal-gap-x',
          `${(wrapperWidth - contentWidth) / 2}px`
        );
      });
    }, 200);
    window.addEventListener('resize', onResize);
    onResize();

    return () => {
      cancelAnimationFrame(animationFrameId);
      window.removeEventListener('resize', onResize);
    };
  }, []);

  const onTabChange = useCallback(
    (key: SettingTab) => {
      setSettingState({ activeTab: key });
      setMobileSidebarOpen(false);
    },
    [setSettingState]
  );
  // STREAMLAIN: Footer hidden - hooks commented out
  // const [openIssueFeedbackModal, setOpenIssueFeedbackModal] = useState(false);
  // const [openStarAFFiNEModal, setOpenStarAFFiNEModal] = useState(false);

  // const handleOpenIssueFeedbackModal = useCallback(() => {
  //   setOpenIssueFeedbackModal(true);
  // }, [setOpenIssueFeedbackModal]);

  // const handleOpenStarAFFiNEModal = useCallback(() => {
  //   setOpenStarAFFiNEModal(true);
  // }, [setOpenStarAFFiNEModal]);

  const addSubPageIsland = useCallback(() => {
    const island = createIsland();
    setSubPageIslands(prev => [...prev, island]);
    const dispose = () => {
      setSubPageIslands(prev => prev.filter(i => i !== island));
    };
    return { island, dispose };
  }, []);

  const contextValue = useMemo(
    () =>
      ({
        islands: subPageIslands,
        addIsland: addSubPageIsland,
      }) satisfies SubPageContextType,
    [subPageIslands, addSubPageIsland]
  );

  useEffect(() => {
    if (settingState.scrollAnchor) {
      flushSync(() => {
        const target = modalContentRef.current?.querySelector(
          `#${settingState.scrollAnchor}`
        );
        if (target) {
          target.scrollIntoView();
        }
      });
    }
    modalContentWrapperRef.current?.scrollTo({ top: 0 });
  }, [settingState]);
  return (
    <FrameworkScope scope={currentServer.scope}>
      <button
        type="button"
        className={style.mobileSidebarButton}
        aria-label="Settings-Bereiche"
        aria-expanded={mobileSidebarOpen}
        onClick={() => setMobileSidebarOpen(open => !open)}
      >
        <SidebarIcon />
        <span>Bereiche</span>
      </button>
      {mobileSidebarOpen ? (
        <button
          type="button"
          className={style.mobileSidebarScrim}
          aria-label="Settings-Bereiche schliessen"
          onClick={() => setMobileSidebarOpen(false)}
        />
      ) : null}
      <SettingSidebar
        activeTab={settingState.activeTab}
        onTabChange={onTabChange}
        mobileOpen={mobileSidebarOpen}
      />
      <SubPageContext.Provider value={contextValue}>
        <Scrollable.Root>
          <Scrollable.Viewport
            data-testid="setting-modal-content"
            className={style.wrapper}
            ref={modalContentWrapperRef}
            data-setting-page
            data-open
          >
            <div className={style.centerContainer}>
              <div
                ref={modalContentRef}
                className={style.content}
                data-wide={WIDE_TABS.has(settingState.activeTab) || undefined}
              >
                <SWRErrorBoundary
                  FallbackComponent={SettingsPanelErrorFallback}
                >
                  <Suspense fallback={<WorkspaceDetailSkeleton />}>
                    {settingState.activeTab === 'account' &&
                    loginStatus === 'authenticated' ? (
                      <AccountSetting onChangeSettingState={setSettingState} />
                    ) : isWorkspaceSetting(settingState.activeTab) ? (
                      <WorkspaceSetting
                        activeTab={settingState.activeTab}
                        onCloseSetting={onCloseSetting}
                        onChangeSettingState={setSettingState}
                      />
                    ) : !isWorkspaceSetting(settingState.activeTab) ? (
                      <GeneralSetting
                        activeTab={settingState.activeTab}
                        mailAccountId={mailAccountId}
                      />
                    ) : null}
                  </Suspense>
                </SWRErrorBoundary>
              </div>
              {/* STREAMLAIN: Footer hidden - links to AFFiNE GitHub/Issues
              <div className={style.footer}>
                <ContactWithUsIcon fontSize={16} />
                <Trans
                  i18nKey={'com.streamlain.settings.suggestion-2'}
                  components={{
                    1: (
                      <span
                        className={style.link}
                        onClick={handleOpenStarAFFiNEModal}
                      />
                    ),
                    2: (
                      <span
                        className={style.link}
                        onClick={handleOpenIssueFeedbackModal}
                      />
                    ),
                  }}
                />
              </div>
              <StarAFFiNEModal
                open={openStarAFFiNEModal}
                setOpen={setOpenStarAFFiNEModal}
              />
              <IssueFeedbackModal
                open={openIssueFeedbackModal}
                setOpen={setOpenIssueFeedbackModal}
              />
*/}
            </div>
            <Scrollable.Scrollbar />
          </Scrollable.Viewport>
          <SubPageTarget />
        </Scrollable.Root>
      </SubPageContext.Provider>
    </FrameworkScope>
  );
};

export const SettingDialog = ({
  close,
  activeTab,
  mailAccountId,
  scrollAnchor,
}: DialogComponentProps<WORKSPACE_DIALOG_SCHEMA['setting']>) => {
  return (
    <Modal
      width={1280}
      height={920}
      contentOptions={{
        ['data-testid' as string]: 'setting-modal',
        className: style.modalContent,
        style: {
          maxHeight: 'min(920px, 85vh)',
          maxWidth: 'min(1280px, calc(100dvw - 100px))',
          padding: 0,
          overflow: 'hidden',
          display: 'flex',
        },
      }}
      open
      onOpenChange={() => close()}
      closeButtonOptions={{
        style: { right: 14, top: 14 },
      }}
    >
      <Suspense fallback={<CenteredLoading />}>
        <SettingModalInner
          activeTab={activeTab}
          mailAccountId={mailAccountId}
          onCloseSetting={close}
          scrollAnchor={scrollAnchor}
        />
      </Suspense>
    </Modal>
  );
};
